import { useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Button } from '@shared/components/ui/button';
import { BookingPageLayout } from '@features/booking/layouts';
import { useBookingFlow } from '@features/booking/flow';
import { useBooking } from '@features/booking/state';
import { useRestaurant } from '@core/tenant/TenantProvider';
import { useLanguage } from '@core/i18n';
import { fetchWaitingListGuaranteeRules } from '@features/booking/api/waitingList';
import { captureApiError } from '@core/sentry';
import type { ServiceType } from '@features/booking/state/types';

const WaitingListAlternativesPage = () => {
  const { t } = useTranslation('booking');
  const { goToNext } = useBookingFlow();
  const booking = useBooking();
  const restaurant = useRestaurant();
  const { dateLocale } = useLanguage();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const alternatives = booking.wlAlternatives ?? [];
  const hasAlternatives = alternatives.length > 0;

  // PATH A: select an alternative → switch to standard flow.
  // flushSync so the orchestrator recomputes active steps with flowType='standard'
  // before goToNext() resolves the next active step.
  // updateBooking is a shallow merge, so any state set by a prior "Continue with WL
  // anyway" (or back-nav from /payment after PATH B) leaks into the standard flow.
  // We reset:
  //   - guaranteeType + feeDeposit: prevent the ConfirmationPage guard from false-
  //     positive on stale WL values when the picked alternative is a traditional slot.
  //   - cardToken + paymentMethod: the token was tokenized for a different intent
  //     (waitlist guarantee for the dates in WL_FORM). The user is now booking a
  //     specific alternative slot — re-tokenizing makes the orchestrator route
  //     through /payment so guaranteeType/feeDeposit get re-set from the slot data,
  //     which is what handleConfirm reads to choose the deposit vs traditional path.
  const handleSelectAlternative = (date: string, serviceType: string, hour: string) => {
    flushSync(() => {
      booking.updateBooking({
        flowType: 'standard',
        selectedDate: date,
        service: serviceType as ServiceType,
        selectedTime: hour,
        suggestedFromWaitingListAlternatives: true,
        guaranteeType: null,
        feeDeposit: 0,
        cardToken: null,
        paymentMethod: null,
      });
    });
    goToNext();
  };

  // Show alternatives (page only renders when alternatives exist via step condition)
  return (
    <BookingPageLayout
      title={t('NEW_MODAL_NO_NEED_TO_WAIT')}
      subtitle={t('NEW_MODAL_SELECT_ONE_OPTION_BELOW')}
    >
      <div className="w-full px-6 space-y-4">
        {alternatives.map((alt) => (
          <div key={`${alt.date}-${alt.serviceType}`} className="bg-foreground/5 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-foreground">
                  {format(new Date(alt.date + 'T12:00:00'), 'EEEE, MMMM d', { locale: dateLocale })}
                </p>
                <p className="text-sm text-foreground/50 capitalize">{alt.serviceType}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {alt.hours.map((hour) => (
                <Button
                  key={hour}
                  variant="outline"
                  size="sm"
                  className="rounded-xl border-brand text-brand hover:bg-brand hover:text-brand-foreground"
                  onClick={() => handleSelectAlternative(alt.date, alt.serviceType, hour)}
                >
                  {hour}
                </Button>
              ))}
            </div>
          </div>
        ))}

        {/* Option to continue with WL anyway */}
        <div className="pt-4 border-t">
          <p className="text-sm text-foreground/50 text-center mb-3">
            {t('WAITING_LIST_NO_ALTERNATIVES')}
          </p>
          <Button
            variant="outline"
            className="w-full rounded-xl"
            onClick={async () => {
              setIsLoading(true);
              setError(null);
              try {
                const rules = await fetchWaitingListGuaranteeRules({
                  dates: booking.wlDates,
                  restId: restaurant.id,
                  serviceType: booking.wlServiceType ?? '-1',
                  pax: booking.diners,
                  menus: booking.wlMenus,
                  hour: booking.wlPreferredTime ?? '-1',
                });

                // Legacy always uses no_show for WL (setGuaranteeInfoWlCalendar)
                const wlGuaranteeType = (rules.type === 'deposit' || rules.type === 'no_show')
                  ? 'no_show' : 'traditional';
                // flushSync so the orchestrator sees guaranteeType + feeDeposit and
                // includes the GUARANTEE step before goToNext() picks the next path.
                flushSync(() => {
                  booking.updateBooking({
                    guaranteeType: wlGuaranteeType,
                    feeDeposit: rules.fee,
                    tipChargePercent: 0,
                    feeGratuity: 0,
                  });
                });

                goToNext();
              } catch (err) {
                captureApiError(err, { flow: 'wl-alternatives-guarantee' });
                setError(t('CONFIRMATION_ERROR'));
              } finally {
                setIsLoading(false);
              }
            }}
            disabled={isLoading}
          >
            {isLoading
              ? t('CONFIRMATION_SUBMITTING')
              : t('WAITING_LIST_CONTINUE_WITH_WL')}
          </Button>
        </div>

        {error && (
          <div className="bg-red-50 text-red-700 rounded-xl p-4 text-sm">
            {error}
          </div>
        )}
      </div>
    </BookingPageLayout>
  );
};

export default WaitingListAlternativesPage;
